import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANGES = [
    "Ball Valves — API 6D",
    "Gate / Globe / Check — API 6A",
    "Butterfly Valves",
    "Actuated Packages",
    "General Enquiry",
];

const INPUT_CLS =
    "w-full border border-steel bg-obsidian-900 px-4 py-3.5 font-body text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-tactical";

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        company: "",
        email: "",
        range: RANGES[0],
        message: "",
    });
    const [sending, setSending] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success("Enquiry received — our engineering team will respond shortly.");
            setForm({ name: "", company: "", email: "", range: RANGES[0], message: "" });
        } catch {
            toast.error("Could not send enquiry. Please try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="relative">
            <div className="mx-auto grid max-w-7xl gap-14 px-6 py-24 sm:px-8 lg:grid-cols-2 lg:gap-20 lg:py-32">
                <div>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.15 }}
                        transition={{ duration: 0.6 }}
                        className="mb-5 flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-tactical"
                    >
                        <span className="h-px w-10 bg-tactical" />
                        RFQ — Procurement &amp; Engineering
                    </motion.p>
                    <motion.h2
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.15 }}
                        transition={{ duration: 0.7, delay: 0.08 }}
                        className="font-display text-6xl font-bold uppercase leading-[0.9] tracking-tight text-slate-100 sm:text-7xl"
                    >
                        Request
                        <br />
                        <span className="text-tactical">a quote.</span>
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.15 }}
                        transition={{ duration: 0.7, delay: 0.16 }}
                        className="mt-6 max-w-md leading-relaxed text-slate-400"
                    >
                        Send your specification — size, pressure class, service
                        conditions — and an engineer (not a sales script) will
                        come back to you with a technical proposal.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.15 }}
                        transition={{ duration: 0.7, delay: 0.24 }}
                        className="mt-10 space-y-5 border-t border-white/10 pt-8"
                    >
                        <div data-testid="contact-email-row">
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                                Enquiries
                            </p>
                            <p className="mt-1 font-mono text-sm text-slate-200">
                                info@blackhawkvalves.com
                            </p>
                        </div>
                        <div data-testid="contact-location-row">
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                                Headquarters
                            </p>
                            <p className="mt-1 font-mono text-sm text-slate-200">
                                United Kingdom
                            </p>
                        </div>
                        <div data-testid="contact-scope-row">
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                                Scope
                            </p>
                            <p className="mt-1 font-mono text-sm text-slate-200">
                                API 6A · API 6D · Actuated Packages
                            </p>
                        </div>
                    </motion.div>
                </div>

                <motion.form
                    data-testid="rfq-form"
                    onSubmit={submit}
                    initial={{ opacity: 0, y: 28 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    className="relative h-fit border border-steel bg-obsidian-800 p-8 sm:p-10"
                >
                    <span className="absolute left-0 top-0 h-4 w-4 border-l-2 border-t-2 border-tactical" />
                    <span className="absolute bottom-0 right-0 h-4 w-4 border-b-2 border-r-2 border-tactical" />
                    <div className="grid gap-5 sm:grid-cols-2">
                        <div>
                            <label htmlFor="rfq-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                                Name *
                            </label>
                            <input
                                id="rfq-name"
                                data-testid="rfq-name-input"
                                required
                                value={form.name}
                                onChange={set("name")}
                                className={INPUT_CLS}
                                placeholder="Jane Smith"
                            />
                        </div>
                        <div>
                            <label htmlFor="rfq-company" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                                Company
                            </label>
                            <input
                                id="rfq-company"
                                data-testid="rfq-company-input"
                                value={form.company}
                                onChange={set("company")}
                                className={INPUT_CLS}
                                placeholder="EPC / Operator"
                            />
                        </div>
                        <div>
                            <label htmlFor="rfq-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                                Email *
                            </label>
                            <input
                                id="rfq-email"
                                data-testid="rfq-email-input"
                                type="email"
                                required
                                value={form.email}
                                onChange={set("email")}
                                className={INPUT_CLS}
                                placeholder="you@company.com"
                            />
                        </div>
                        <div>
                            <label htmlFor="rfq-range" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                                Valve Range
                            </label>
                            <select
                                id="rfq-range"
                                data-testid="rfq-range-select"
                                value={form.range}
                                onChange={set("range")}
                                className={`${INPUT_CLS} appearance-none`}
                            >
                                {RANGES.map((r) => (
                                    <option key={r} value={r} className="bg-obsidian">
                                        {r}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="sm:col-span-2">
                            <label htmlFor="rfq-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                                Specification / Message *
                            </label>
                            <textarea
                                id="rfq-message"
                                data-testid="rfq-message-input"
                                required
                                rows={4}
                                value={form.message}
                                onChange={set("message")}
                                className={`${INPUT_CLS} resize-none`}
                                placeholder='e.g. 10" class 600 trunnion ball valve, sour service, ESD actuated'
                            />
                        </div>
                    </div>
                    <button
                        data-testid="rfq-submit-button"
                        type="submit"
                        disabled={sending}
                        className="group relative mt-7 w-full overflow-hidden bg-tactical px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.25em] text-obsidian transition-all duration-300 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        <span className="relative z-10">
                            {sending ? "Sending…" : "Send Enquiry"}
                        </span>
                        <span className="absolute inset-0 -translate-x-full bg-slate-100 transition-transform duration-500 group-hover:translate-x-0" />
                    </button>
                </motion.form>
            </div>
        </section>
    );
}
